import React, { useId, useMemo } from "react";
import { Controller } from "react-hook-form";
import Select, { components, InputProps } from "react-select";
import { SelectFormProps, SelectOptionTypes } from "./types";
import { buildSelectStyles } from "./reactSelectSkin";
import { ErrorMessage } from "./styles";

export const SelectForm = ({
  name,
  control,
  required = true,
  isClearable = false,
  defaultValue = null,
  disabled = false,
  rules,
  options,
  testId,
  id,
  "aria-describedby": ariaDescribedBy,
  menuPlacement = "bottom",
}: SelectFormProps) => {
  const generatedId = useId();
  const inputId = id ?? testId ?? generatedId;
  const errorId = `${inputId}-error`;

  // react-select overwrites aria-describedby on the input, so it's merged via the Input slot instead.
  // Memoised because a new Input identity remounts the slot, dropping focus and closing the open menu.
  const selectComponents = useMemo(
    () => ({
      Input: (props: InputProps<SelectOptionTypes, false>) => {
        const existingDescription = props["aria-describedby"];
        const describedBy = [existingDescription, ariaDescribedBy]
          .filter(Boolean)
          .join(" ");

        return (
          <components.Input
            {...props}
            aria-describedby={describedBy || undefined}
          />
        );
      },
    }),
    [ariaDescribedBy],
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({
        field: { onChange, value },
        fieldState: { error },
        formState,
      }) => (
        <div>
          <Select
            inputId={inputId}
            isDisabled={disabled}
            required={required}
            isClearable={isClearable}
            aria-invalid={Boolean(error) || undefined}
            aria-errormessage={error ? errorId : undefined}
            aria-describedby={ariaDescribedBy}
            value={value}
            options={options}
            onChange={onChange}
            menuPortalTarget={document.body}
            menuPlacement={menuPlacement}
            components={selectComponents}
            styles={buildSelectStyles<SelectOptionTypes>({
              hasError: Boolean(error),
              disabled,
            })}
          />
          {error && (
            <ErrorMessage id={errorId} role="alert">
              {error.message}
            </ErrorMessage>
          )}
        </div>
      )}
    />
  );
};
